import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { INTAKE } from "@/lib/vitalityData";

function Field({ f, value, onChange }) {
  if (f.kind === "number") {
    return (
      <input
        type="number" value={value || ""} placeholder={f.placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-40 rounded-2xl border border-[var(--line)] bg-white px-4 py-3 text-base outline-none focus:border-[#4CAF7D]"
        data-testid={`vf-${f.id}`}
      />
    );
  }
  if (f.kind === "radio") {
    return (
      <div className="flex flex-wrap gap-2">
        {f.options.map((o) => (
          <button
            key={o.v} onClick={() => onChange(o.v)}
            className="rounded-full px-4 py-2.5 text-sm font-medium transition-all"
              style={value === o.v
              ? { background: "#1E6B45", color: "#fff", boxShadow: "0 10px 24px -10px rgba(30,107,69,0.7)" }
              : { background: "rgba(255,255,255,0.7)", border: "1px solid var(--line)" }}
            data-testid={`vf-${f.id}-${o.v}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  const arr = value || [];
  return (
    <div className="flex flex-wrap gap-2">
      {f.options.map((o) => {
        const on = arr.includes(o.v);
        return (
          <button
            key={o.v}
            onClick={() => onChange(on ? arr.filter((x) => x !== o.v) : [...arr, o.v])}
            className="rounded-full px-4 py-2.5 text-sm font-medium transition-all inline-flex items-center gap-1.5"
            style={on ? { background: "#4CAF7D", color: "#fff" } : { background: "rgba(255,255,255,0.7)", border: "1px solid var(--line)" }}
            data-testid={`vf-${f.id}-${o.v}`}
          >
            {on && <Check size={12} />}{o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function VitalityIntake({ initial, onDone, onCancel }) {
  const [step, setStep] = useState(0);
  const [v, setV] = useState(initial || {});
  const s = INTAKE[step];
  const ready = s.fields.every((f) => f.optional || f.kind === "multi" || v[f.id] != null);

  const next = () => {
    if (step + 1 >= INTAKE.length) onDone(v);
    else setStep(step + 1);
  };

  return (
    <div className="fixed inset-0 z-[80] overflow-y-auto" style={{ background: "#EEF6EF" }} data-testid="vitality-intake">
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(50% 40% at 10% 0%, rgba(76,175,125,0.18), transparent 70%), radial-gradient(45% 35% at 95% 100%, rgba(226,109,90,0.12), transparent 70%)" }} />
      <div className="relative max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="rounded-[1.6rem] p-6 sm:p-8 mb-10 relative overflow-hidden" style={{ background: "linear-gradient(150deg, #0F3324, #1C5637)" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {INTAKE.map((x, i) => (
                <span key={x.id} className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full text-[11px] font-semibold flex items-center justify-center" style={i <= step ? { background: "#A8E06A", color: "#0F3324" } : { background: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.6)" }}>{i + 1}</span>
                  {i < 2 && <span className="w-8 h-px" style={{ background: i < step ? "#A8E06A" : "rgba(255,255,255,0.2)" }} />}
                </span>
              ))}
            </div>
            <button onClick={onCancel} className="text-xs text-white/50 hover:text-white" data-testid="vitality-intake-cancel">leave</button>
          </div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-white/45 mt-7">Question {step + 1} of 3</p>
          <h2 className="font-display text-4xl sm:text-5xl mt-2 leading-[1.03] text-white">{s.title}</h2>
          <p className="text-white/60 mt-3 max-w-xl">{s.lead}</p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={s.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }} transition={{ duration: 0.35 }}>
            <div className="space-y-8">
              {s.fields.map((f) => (
                <div key={f.id}>
                  <p className="text-sm font-semibold mb-3">{f.label}{f.optional && <span className="text-[var(--ink-soft)] font-normal"> · optional</span>}</p>
                  <Field f={f} value={v[f.id]} onChange={(val) => setV({ ...v, [f.id]: val })} />
                </div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-12 flex items-center justify-between">
          <button onClick={() => (step > 0 ? setStep(step - 1) : onCancel())} className="text-sm text-[var(--ink-soft)] hover:text-[var(--ink)] inline-flex items-center gap-1.5" data-testid="vitality-intake-back">
            <ArrowLeft size={13} /> {step > 0 ? "back" : "leave"}
          </button>
          <button
            onClick={next} disabled={!ready}
            className="inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-semibold text-white transition-all disabled:opacity-40"
            style={{ background: "#1E6B45", boxShadow: ready ? "0 16px 40px -16px rgba(43,125,84,0.8)" : "none" }}
            data-testid="vitality-intake-next"
          >
            {step === 2 ? "Build the twelve weeks" : "Next"} <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
